import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Predefined accent schemes for light/dark modes
const ACCENT_SCHEMES = {
  emerald: {
    id: 'emerald',
    label: 'Emerald & Orange',
    light: {
      primary: 'bengal-green',
      secondary: 'bengal-orange'
    },
    dark: {
      primary: 'emerald-400',
      secondary: 'orange-400'
    }
  },
  ocean: {
    id: 'ocean',
    label: 'Blue & Teal',
    light: {
      primary: 'blue-600',
      secondary: 'teal-400'
    },
    dark: {
      primary: 'blue-400',
      secondary: 'teal-300'
    }
  },
  magenta: {
    id: 'magenta',
    label: 'Purple & Pink',
    light: {
      primary: 'purple-600',
      secondary: 'pink-500'
    },
    dark: {
      primary: 'purple-400',
      secondary: 'pink-300'
    }
  }
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light') // 'light' | 'dark'
  const [systemPrefersDark, setSystemPrefersDark] = useState(false)
  const [accentId, setAccentId] = useState('emerald')

  // Apply theme class and smooth transitions
  const applyTheme = useCallback((newTheme) => {
    const root = document.documentElement
    if (newTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Smooth transition for background and text when toggling
    root.style.transition = 'background-color 0.3s ease, color 0.3s ease'
    window.setTimeout(() => {
      root.style.transition = ''
    }, 400)
  }, [])

  // Initialize from localStorage or system settings
  useEffect(() => {
    const savedTheme = localStorage.getItem('shikdar_films_theme')
    const savedAccent = localStorage.getItem('shikdar_films_accent') || 'emerald'

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const prefersDark = mediaQuery.matches
    setSystemPrefersDark(prefersDark)

    const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light')
    setTheme(initialTheme)
    applyTheme(initialTheme)

    if (ACCENT_SCHEMES[savedAccent]) {
      setAccentId(savedAccent)
    }

    const listener = (event) => {
      setSystemPrefersDark(event.matches)
      // Only auto-change theme if user hasn't explicitly chosen one
      const userTheme = localStorage.getItem('shikdar_films_theme')
      if (!userTheme) {
        const nextTheme = event.matches ? 'dark' : 'light'
        setTheme(nextTheme)
        applyTheme(nextTheme)
      }
    }

    mediaQuery.addEventListener('change', listener)
    return () => mediaQuery.removeEventListener('change', listener)
  }, [applyTheme])

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    applyTheme(newTheme)
    localStorage.setItem('shikdar_films_theme', newTheme)
  }

  const setLightTheme = () => {
    setTheme('light')
    applyTheme('light')
    localStorage.setItem('shikdar_films_theme', 'light')
  }

  const setDarkTheme = () => {
    setTheme('dark')
    applyTheme('dark')
    localStorage.setItem('shikdar_films_theme', 'dark')
  }

  const followSystemTheme = () => {
    const nextTheme = systemPrefersDark ? 'dark' : 'light'
    setTheme(nextTheme)
    applyTheme(nextTheme)
    localStorage.removeItem('shikdar_films_theme')
  }

  const setAccentScheme = (id) => {
    if (!ACCENT_SCHEMES[id]) return
    setAccentId(id)
    localStorage.setItem('shikdar_films_accent', id)
  }

  const currentAccent = ACCENT_SCHEMES[accentId] || ACCENT_SCHEMES.emerald
  const activeAccentColors = theme === 'dark' ? currentAccent.dark : currentAccent.light

  const value = {
    theme,
    isDark: theme === 'dark',
    systemPrefersDark,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    followSystemTheme,
    accentId,
    setAccentScheme,
    accentScheme: currentAccent,
    accentColors: activeAccentColors,
    ACCENT_SCHEMES
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}